"use client";

import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { StudentSearchResult } from "@/features/workspaces/api/searchStudents";

interface EmailConfirmationScreenProps {
  students: StudentSearchResult[];
  description?: string;
}

export function EmailConfirmationScreen({ students, description }: EmailConfirmationScreenProps) {
  const { t } = useTranslation(["workspaces", "common"]);

  return (
    <Card className="border-opacity-50 mx-auto flex h-full w-full flex-col border-none bg-transparent">
      <CardHeader className="flex-shrink-0 space-y-2 p-2 text-center">
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-500/20 to-emerald-600/20 backdrop-blur-sm max-sm:h-12 max-sm:w-12"
          initial={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Icon className="text-green-500" height="2rem" icon="tabler:check" width="2rem" />
        </motion.div>
        <CardTitle className="text-accent-foreground text-xl leading-tight font-bold max-sm:text-lg">
          {t("workspaces:emailInviteConfirmation")}
        </CardTitle>
        <CardDescription className="text-accent-foreground/70 text-sm max-sm:text-xs">
          {t("workspaces:emailInviteConfirmationDescription")}
        </CardDescription>
      </CardHeader>

      <CardContent className="min-h-0 flex-1 px-0">
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="flex h-full flex-col rounded-xl bg-gradient-to-br from-blue-600/4 to-blue-500/2 p-6 backdrop-blur-sm max-sm:p-4"
          initial={{ opacity: 0, scale: 0.95 }}
          transition={{ delay: 0.2, duration: 0.3, ease: "easeOut" }}
        >
          <div className="mb-4 flex flex-shrink-0 items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
              <Icon
                className="text-blue-500"
                height="1.25rem"
                icon="tabler:users"
                width="1.25rem"
              />
            </div>
            <div>
              <p className="text-accent-foreground text-base font-semibold">
                {t("workspaces:emailSentTo")}
              </p>
            </div>
          </div>

          <div className="bg-background/60 min-h-0 flex-1 rounded-lg p-4 backdrop-blur-sm">
            <ScrollArea className="h-40">
              <div className="space-y-3 pr-2">
                {students.map((student, index) => (
                  <motion.div
                    key={student.id}
                    animate={{ opacity: 1, x: 0 }}
                    className="group bg-muted/30 relative overflow-hidden rounded-lg p-3 backdrop-blur-sm"
                    initial={{ opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {/* Backdrop blur image */}
                    <div className="absolute inset-0 top-1/2 -left-0 h-20 w-20 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 opacity-20">
                      <img
                        alt={student.name}
                        className="h-full w-full object-cover blur-2xl"
                        src={student.avatar_path}
                      />
                    </div>

                    <div className="relative flex items-center gap-3">
                      <div className="bg-muted/40 flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-blue-500/10">
                        <Avatar className="h-full w-full">
                          <AvatarImage alt={student.name} src={student.avatar_path} />
                          <AvatarFallback className="text-sm font-medium">
                            {student.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-accent-foreground truncate text-sm font-medium">
                          {student.name}
                        </h4>
                        <p className="text-muted-foreground text-xs">{t("common:roles.student")}</p>
                      </div>
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20">
                        <Icon
                          className="text-blue-500"
                          height="0.75rem"
                          icon="tabler:check"
                          width="0.75rem"
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {description && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="bg-accent-foreground/5 mt-4 flex-shrink-0 rounded-lg border border-blue-500/5 p-4 backdrop-blur-sm"
              initial={{ opacity: 0, y: 10 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/20">
                  <Icon
                    className="text-blue-500"
                    height="0.875rem"
                    icon="tabler:message"
                    width="0.875rem"
                  />
                </div>
                <div>
                  <p className="text-accent-foreground mb-1 text-sm font-medium">Message</p>
                  <p className="text-accent-foreground/70 text-sm leading-relaxed">{description}</p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </CardContent>
    </Card>
  );
}
